import { useEffect, useState } from "react";
import { Copy, MessageCircle, QrCode, Trash2, Link2, Loader2, Smartphone, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime, imgUrl, isManagerRole } from "@/lib/api";
import { copyText, downloadQr, printPoster, qrDataUrl } from "@/lib/qr";

const linkUrl = (l) => `${window.location.origin}/m/entrar/${l.token}`;

function QrDialog({ link, store, onClose }) {
  const { session } = useAuth();
  const [src, setSrc] = useState("");
  const url = link ? linkUrl(link) : "";
  useEffect(() => { if (url) qrDataUrl(url, 600).then(setSrc); }, [url]);
  const st = session.tenant.settings;
  const poster = () => printPoster({ store, title: "App de comandas", subtitle: "Garçons e operadores: escaneie para entrar pelo celular", url, color: st.brand_color, logo: st.logo_url ? imgUrl(st.logo_url) : "" });
  return (
    <Dialog open={!!link} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="mobile-link-qr-dialog">
        <DialogHeader><DialogTitle>{link?.label}</DialogTitle><DialogDescription>Aponte a câmera do celular para abrir o login do app.</DialogDescription></DialogHeader>
        <div className="mx-auto rounded-2xl border bg-white p-3">{src ? <img src={src} alt="QR Code do link" className="h-64 w-64" data-testid="mobile-link-qr-img" /> : <Loader2 className="m-28 h-6 w-6 animate-spin" />}</div>
        <p className="break-all text-center font-mono text-xs text-muted-foreground">{url}</p>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => downloadQr(url, `qr-app-comandas-${link.label}`)} data-testid="mobile-link-qr-download"><Download className="mr-2 h-4 w-4" />Baixar imagem</Button>
          <Button onClick={poster} data-testid="mobile-link-qr-print"><Printer className="mr-2 h-4 w-4" />Imprimir cartaz</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkRow({ l, store, onQr, onDelete }) {
  const url = linkUrl(l);
  const copy = async () => { await copyText(url); toast.success("Link copiado"); };
  const msg = `Acesse o app de comandas da ${store} pelo celular: ${url}\nEntre com o seu usuário e senha.`;
  return (
    <li className="card-surface space-y-3 p-4" data-testid={`mobile-link-${l.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Link2 className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{l.label}</p>
          <p className="truncate font-mono text-xs text-primary" data-testid={`mobile-link-url-${l.id}`}>{url}</p>
          <p className="text-xs text-muted-foreground">Gerado por {l.created_by} em {fmtDateTime(l.created_at)} · aberto {l.opens || 0} vez(es){l.last_open_at ? ` · último ${fmtDateTime(l.last_open_at)}` : ""}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={copy} data-testid={`mobile-link-copy-${l.id}`}><Copy className="mr-1 h-4 w-4" />Copiar link</Button>
        <Button size="sm" asChild className="bg-[#16a34a] text-white hover:bg-[#15803d]"><a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid={`mobile-link-whatsapp-${l.id}`}><MessageCircle className="mr-1 h-4 w-4" />WhatsApp</a></Button>
        <Button size="sm" variant="outline" onClick={() => onQr(l)} data-testid={`mobile-link-qr-${l.id}`}><QrCode className="mr-1 h-4 w-4" />QR Code</Button>
        <Button size="sm" variant="ghost" className="ml-auto text-red-500" onClick={() => onDelete(l)} data-testid={`mobile-link-delete-${l.id}`}><Trash2 className="mr-1 h-4 w-4" />Excluir</Button>
      </div>
    </li>
  );
}

export default function MobileLinks() {
  const { session } = useAuth();
  const store = session.tenant.nome_fantasia;
  const [links, setLinks] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const allowed = isManagerRole(session.user);
  const load = () => api.get("/mobile-links").then((r) => setLinks(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (allowed) load(); }, [allowed]);
  if (!allowed) return <p className="p-6 text-muted-foreground" data-testid="mobile-links-denied">Só o proprietário, administradores e gerentes podem gerar o link do app.</p>;
  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/mobile-links", { label }); setLabel(""); toast.success("Link gerado"); setLinks((x) => [data, ...(x || [])]); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Excluir o link "${l.label}"? Quem abrir esse link não vai conseguir mais entrar por ele.`)) return;
    try { await api.delete(`/mobile-links/${l.id}`); toast.success("Link excluído"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="mobile-links-page">
      <PageHeader eyebrow="PDV" title="Link do app (celular)" subtitle="Gere um link para garçons e operadores abrirem o app de comandas direto na tela de login, já com a loja preenchida." />
      <div className="card-surface flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
        <span className="hidden h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground sm:grid"><Smartphone className="h-6 w-6" /></span>
        <label className="flex-1 space-y-1.5 text-sm"><span className="font-medium">Nome do link (opcional)</span>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} placeholder="Ex.: Garçons do salão" data-testid="mobile-link-label-input" /></label>
        <Button onClick={create} disabled={busy} className="h-10" data-testid="mobile-link-create-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}Gerar link</Button>
      </div>
      <p className="text-xs text-muted-foreground">Os links continuam valendo mesmo depois de gerar outros. Quem abre o link ainda precisa entrar com o próprio usuário e senha. Gere os links pelo endereço que a equipe vai usar (o endereço publicado do sistema).</p>
      {!links ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : !links.length ? (
        <p className="card-surface p-8 text-center text-sm text-muted-foreground" data-testid="mobile-links-empty">Nenhum link gerado ainda. Toque em "Gerar link" para criar o primeiro.</p>
      ) : (
        <ul className="space-y-3" data-testid="mobile-links-list">{links.map((l) => <LinkRow key={l.id} l={l} store={store} onQr={setQr} onDelete={remove} />)}</ul>
      )}
      <QrDialog link={qr} store={store} onClose={() => setQr(null)} />
    </div>
  );
}
